/** @jsx jsx */
import { jsx, useThemeUI } from "theme-ui"
import { useState, useEffect, useRef, useMemo } from "react"
import { ab } from "../../theme"
import Markdown from "react-markdown"
import { DownloadIcon } from "../../assets/icons"

const ProductDataEntries = ({
  title,
  description,
  entries,
  loading,
  error = null,
  children,
  productPerPage = 10,
  tableHeaderProduct = "Produkt",
  tableHeaderKennzeichnung = "Kennzeichnung",
  tableHeaderCertificate = "Zertifikat",
  tableHeaderDate = "Eingangsdatum",
  noResultsText = "Keine Einträge gefunden.",
  productInput,
  kennzeichnungInput,
}) => {
  const { rawColors: colors } = useThemeUI().theme
  const [currentPage, setCurrentPage] = useState(1)
  const tableRef = useRef(null)
  const dataDiv = useRef(null)

  const numPages = Math.ceil(entries.length / productPerPage)
  const pagingLimit = 4
  const pagingPreviewWidth = 1

  // Reset to first page whenever the entries (search/filter result) change
  useEffect(() => {
    setCurrentPage(1)
  }, [entries])

  // Clamp the page in case filtering shrank the list below the current page
  const safePage = Math.min(currentPage, Math.max(numPages, 1))

  // Derive the visible slice synchronously so the table always reflects the
  // current filtered + sorted entries (no async timeout = no stale rows).
  const currentEntries = useMemo(() => {
    const startIndex = (safePage - 1) * productPerPage
    return entries.slice(startIndex, startIndex + productPerPage)
  }, [entries, safePage, productPerPage])

  // Build the page-number controls
  const paging = useMemo(() => {
    let pagingStart = 0
    let pagingEnd = numPages

    if (numPages > pagingLimit) {
      if (safePage >= pagingLimit - pagingPreviewWidth) {
        pagingStart = safePage - pagingPreviewWidth - 1
        pagingEnd = safePage + pagingPreviewWidth
        pagingStart =
          pagingEnd > numPages
            ? pagingStart - pagingEnd + numPages
            : pagingStart
        pagingEnd = pagingEnd > numPages ? numPages : pagingEnd
      } else {
        pagingEnd = pagingLimit
      }
    }

    const pages = []
    for (let i = pagingStart; i < pagingEnd; i++) {
      pages.push(
        <span
          key={i}
          title={i + 1}
          onClick={() => {
            setCurrentPage(i + 1)
            scrollToTop()
          }}
          className={safePage === i + 1 ? "current" : ""}
          sx={{ cursor: "pointer", mx: 1, px: 2 }}
        >
          {i + 1}
        </span>
      )
    }
    return pages
  }, [numPages, safePage])

  const scrollToTop = () => {
    if (dataDiv.current) dataDiv.current.scrollIntoView()
  }

  // Navigation buttons
  const goToFirstPage = () => {
    setCurrentPage(1)
    scrollToTop()
  }
  const goToLastPage = () => {
    setCurrentPage(numPages)
    scrollToTop()
  }
  const prevPage = () => {
    if (safePage > 1) {
      setCurrentPage(safePage - 1)
      scrollToTop()
    }
  }
  const nextPage = () => {
    if (safePage < numPages) {
      setCurrentPage(safePage + 1)
      scrollToTop()
    }
  }

  const loadingTable = loading

  return (
    <div
      className="product-data-entries"
      ref={dataDiv}
      sx={{
        px: ab({ _: 3, md: 3 }),
        py: 3,
        color: "text",
      }}
    >
      {loadingTable ? (
        <div
          sx={{
            textAlign: "center",
            py: 4,
          }}
        >
          Loading entries...
        </div>
      ) : error ? (
        <div
          sx={{
            color: "red",
            p: 3,
            border: "1px solid red",
            borderRadius: "4px",
            bg: "rgba(255,0,0,0.05)",
          }}
        >
          Error loading entries: {error}
        </div>
      ) : (
        <div>
          {entries.length === 0 ? (
            <p>{noResultsText}</p>
          ) : (
            <div>
              <div
                sx={{
                  overflowX: "auto",
                  width: "100%",
                }}
              >
                <table
                  ref={tableRef}
                  sx={{
                    width: "100%",
                    borderCollapse: "collapse",
                    "& th": {
                      px: 3,
                      py: 3,
                      bg: colors.modes["gutesiegel"].primary,
                      color: "#fff",
                      textAlign: "left",
                      fontWeight: "bold",
                    },
                    "& tr:nth-of-type(odd)": {
                      bg: "rgb(242, 246, 249)",
                    },
                    "& tr:nth-of-type(even)": {
                      bg: "rgb(220, 230, 239)",
                    },
                    "& td": {
                      px: 3,
                      py: 3,
                      verticalAlign: "middle",
                      borderTop: "1px solid #ddd",
                    },
                    "& td:first-of-type": {
                      textAlign: "left",
                      pl: "15px",
                      hyphens: "none",
                    },
                  }}
                >
                  <thead
                    className="table-header"
                    sx={{
                      backgroundColor: colors.modes["group"].primary,
                      color: "#fff",
                      tr: {
                        bg: colors.modes["group"].primary + " !important",
                        color: "#fff",
                      },
                    }}
                  >
                    <tr>
                      <th>{tableHeaderProduct}</th>
                      <th>{tableHeaderKennzeichnung}</th>
                      <th>{tableHeaderCertificate}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentEntries &&
                      currentEntries.map((entry, idx) => {
                        //  const dimensionData = parseDimension(entry);
                        return (
                          <tr
                            key={
                              entry.id ||
                              `${entry.name}-${entry.kennzeichnung}-${idx}`
                            }
                          >
                            <td>{entry.name}</td>
                            <td>{entry.kennzeichnung}</td>
                            <td
                              sx={{
                                textAlign: "center",
                              }}
                            >
                              {entry.certificateLink && entry.certificateLink !== "n/a" ? (
                                <a
                                  href={entry.certificateLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    textDecoration: "none",
                                    alignItems: "center",
                                    maxWidth: "30px",
                                    width: "30px",
                                    display: "inline-flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <DownloadIcon sx={{ width: "20px" }} />
                                </a>
                              ) : (
                                ""
                              )}
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {numPages > 0 && (
                <div
                  className="pagination"
                  sx={{
                    textAlign: "center",
                    pt: 4,
                    "& span": {
                      variant: "styles.linkbtmbrd",
                      display: "inline-block",
                      px: 2,
                      cursor: "pointer",
                      "&.current": {
                        borderBottom: "2px solid",
                        borderColor: colors.modes.gutesiegel.primary,
                      },
                    },
                  }}
                >
                  <div
                    sx={{ display: "inline" }}
                    onClick={goToFirstPage}
                    role="button"
                    tabIndex={0}
                  >
                    <span>❮❮</span>
                  </div>
                  <div
                    sx={{ display: "inline" }}
                    onClick={prevPage}
                    role="button"
                    tabIndex={0}
                  >
                    <span>❮</span>
                  </div>
                  <div sx={{ display: "inline" }}>{paging}</div>
                  <div
                    sx={{ display: "inline" }}
                    onClick={nextPage}
                    role="button"
                    tabIndex={0}
                  >
                    <span>❯</span>
                  </div>
                  <div
                    sx={{ display: "inline" }}
                    onClick={goToLastPage}
                    role="button"
                    tabIndex={0}
                  >
                    <span>❯❯</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ProductDataEntries
